import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormAttachmentUploadRequirements } from '../attachments/form-attachment-requirements';
import { FormStep } from '../form-steps.interface';
import { GeneralFormData } from '../general-form-data.model';
import { parseLocalDate } from '../../../utils/date-time-formatter';


export class AerialAndScissorLift {
    id?: string;
    submittedByName: string = '';
    submittedByEmail: string = '';
    submissionDate?: Date;
    location: string = '';
    unitNumber: string = '';
    typeOfEquipment: string = '';
    jobNumber: string = '';
    inspectedBy: string = '';
    date: Date | null = null;
    // Items to be Checked - All radio button fields
    fuelLevel: string = '';
    engineOilLevel: string = '';
    hydraulicSystemLevel: string = '';
    batteryCondition: string = '';
    tiresCondition: string = '';
    platformStructure: string = '';
    instructionPlacards: string = '';
    eyeWashFireExtinguisher: string = '';
    tiltAlarm: string = '';
    // From the Ground Control Station
    groundRaiseLowerPlatform: string = '';
    groundAuxiliaryPower: string = '';
    groundTelescopeOutIn: string = '';
    groundSwingRightLeft: string = '';
    // From the Platform Control Station
    platformSafetyHarness: string = '';
    platformTelescopeOutIn: string = '';
    platformRaiseLowerPlatform: string = '';
    platformSwingRightLeft: string = '';
    extendLevelOutriggers: string = '';
    driveMachineForwardReverse: string = '';
    platformAuxiliaryPower: string = '';
    areaInspected: string = '';
    otherRepairs: string = '';
    inspectionSignature: Array<string> = [];
 
    constructor(data?: Partial<AerialAndScissorLift>) {
        if (data) {
            Object.assign(this, {
                ...data,
                date: data.date ? parseLocalDate(data.date as any) : null,
                submissionDate: data.submissionDate ? new Date(data.submissionDate) : null,
                inspectionSignature: data.inspectionSignature ? data.inspectionSignature : []
            });
        }
    }

    static getFormSteps(): FormStep[] {
        return [
            {
                id: 'basic',
                shortTitle: 'Basic Info',
                title: 'Basic Information',
                icon: 'person-outline',
                description: 'Enter the daily aerial and scissor lift inspection details',
                sections: [{
                    fields: ['location', 'unitNumber', 'typeOfEquipment', 'jobNumber', 'inspectedBy', 'date']
                }
            ]
             
            },
            {
                id: 'check-items',
                shortTitle: 'Check Items',
                title: 'Items to be Checked',
                icon: 'build-outline',
                description: 'Select Satisfactory or Needs Repair',
                sections: [{
                    fields: ['fuelLevel', 'engineOilLevel', 'hydraulicSystemLevel', 'batteryCondition', 'tiresCondition', 'platformStructure', 'instructionPlacards', 'eyeWashFireExtinguisher', 'tiltAlarm']
                }
            ]
            },
            {
                id: 'ground-control-station',
                shortTitle: 'Ground Control',
                title: 'From the Ground Control Station',
                icon: 'telescope-outline',
                sections: [{
                    fields: ['groundRaiseLowerPlatform', 'groundAuxiliaryPower', 'groundTelescopeOutIn', 'groundSwingRightLeft']
                }
            ]
            },
            {
                id: 'platform-control-station',
                shortTitle: 'Platform Control',
                title: 'From the Platform Control Station',
                icon: 'rocket-outline',
                sections: [{
                    fields: ['platformSafetyHarness', 'platformTelescopeOutIn', 'platformRaiseLowerPlatform', 'platformSwingRightLeft', 'extendLevelOutriggers', 'driveMachineForwardReverse', 'platformAuxiliaryPower', 'areaInspected', 'otherRepairs', 'inspectionSignature']
                }
            ]
            }
        ];
    }

    static getFormInfo(): GeneralFormData {
        return {
            formType: 'aerial-scissor-lift-inspection',
            formTitle: 'Daily Aerial and Scissor Lift Inspection',
            formIcon: 'construct-outline',
            cardImage: 'assets/form-card-tiles/aerial-scissor-lift-inspection.png',
            formAttachmentRequirements: {
                signatures: [
                    {
                        identifier: 'inspectionSignature',
                        label: this.getFieldMappings()['inspectionSignature'],
                        required: true
                    }
                ]    
            }
        };
    }

    static getFieldMappings(): Record<string, string> {
        return {
            inspectionSignature: "Signature of Person Completing the Inspection",

            submittedByName: "Submitter Name",
            submittedByEmail: "Submitter Email",
            submissionDate: "Submission Date",
            location: "Location",
            unitNumber: "Unit Number",
            typeOfEquipment: "Type of Equipment",
            jobNumber: "Job Number",
            inspectedBy: "Inspected By",
            date: "Date",
            fuelLevel: "Fuel Level (Applicable for Engine Drive Only)",
            engineOilLevel: "Engine Oil Level (Applicable for Engine Drive Only)",
            hydraulicSystemLevel: "Hydraulic System Level, Visible Leaks",
            batteryCondition: "Battery, Water Level, Condition, State of Charge",
            tiresCondition: "Tires, Proper Inflation, Damage",
            platformStructure: "Platform Structure, Cleanliness, Physical Condition",
            instructionPlacards: "Instruction Placards, in Place and Legible",
            eyeWashFireExtinguisher: "Eye Wash Bottle, Fire Extinguisher (If Applicable)",
            tiltAlarm: "Test the Tilt Alarm (If Applicable)",
            groundRaiseLowerPlatform: "Raise and Lower Platforms/Boom",
            groundAuxiliaryPower: "Raise Platform/Boom and Lower With Auxiliary Power",
            groundTelescopeOutIn: "Telescope Out and In",
            groundSwingRightLeft: "Swing Platform Right and Left",
            platformSafetyHarness: "Fasten Safety Harness",
            platformTelescopeOutIn: "Telescope Out and In",
            platformRaiseLowerPlatform: "Raise and Lower Platform/Boom",
            platformSwingRightLeft: "Swing Right and Left",
            extendLevelOutriggers: "Extend and Level Outriggers (If Applicable)",
            driveMachineForwardReverse: "Drive Machine Forward and Reverse, Right and Left",
            platformAuxiliaryPower: "Raise Platforms/Boom and Descend with Auxiliary Power",
            areaInspected: "Has the Area Been Inspected For: Drop-offs, Dangerous Slopes, Floor Holes, Power Lines, and Cords?",
            otherRepairs: "List Other Repairs Necessary"
        };
    }
}
